import React, { useEffect, useState } from 'react';
import { getAuth } from 'firebase/auth';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  onSnapshot,
  updateDoc,
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase'; // Asegúrate que esta ruta sea correcta
import { getRemaining } from '../../utils/invoices';
import {
  EyeIcon,
  CreditCardIcon,
  XMarkIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

const ClientsDashboard = () => {
  const [userProfile, setUserProfile] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showAdminNotification, setShowAdminNotification] = useState(false);

  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const navigate = useNavigate();

  // 🔍 Abrir/Cerrar modal de factura
  const handleOpenModal = (invoice) => setSelectedInvoice(invoice);
  const handleCloseModal = () => setSelectedInvoice(null);

  const generatePDF = () => {
    console.log('✅ Botón PDF clickeado'); // 👈 este debe salir sí o sí
    const element = document.getElementById('invoice-pdf');
  
    if (!element) {
      alert('No se encontró el contenedor de la factura.');
      return;
    }
  
    import('html2pdf.js').then((html2pdf) => {
      html2pdf.default().from(element).save('factura-puertoricowindows.pdf');
    });
  };
  
  

  // 📬 Notificaciones de administrador en tiempo real
  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;

    const q = query(
      collection(db, 'conversations', user.email.toLowerCase(), 'messages'),
      where('from', '==', 'admin'),
      where('read', '==', false)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const unread = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setShowAdminNotification(unread.length > 0);
    });

    return () => unsubscribe();
  }, []);

  const resolveClientRecord = async (uid, email) => {
    const normalizedEmail = email?.toLowerCase();

    const tryDoc = async (docId) => {
      if (!docId) return null;
      const ref = doc(db, 'clients', docId);
      const snap = await getDoc(ref);
      return snap.exists() ? { id: snap.id, ...snap.data() } : null;
    };

    let record = await tryDoc(uid);
    if (record) return record;

    record = await tryDoc(normalizedEmail);
    if (record) return record;

    if (uid) {
      const uidSnap = await getDocs(query(collection(db, 'clients'), where('uid', '==', uid)));
      if (!uidSnap.empty) {
        const docSnap = uidSnap.docs[0];
        return { id: docSnap.id, ...docSnap.data() };
      }
    }

    if (normalizedEmail) {
      const emailSnap = await getDocs(query(collection(db, 'clients'), where('email', '==', normalizedEmail)));
      if (!emailSnap.empty) {
        const docSnap = emailSnap.docs[0];
        return { id: docSnap.id, ...docSnap.data() };
      }
    }

    return null;
  };

  // 📦 Cargar perfil, facturas y propuestas
  useEffect(() => {
    const fetchClientData = async () => {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        navigate('/login');
        return;
      }

      try {
        const record = await resolveClientRecord(user.uid, user.email);
        if (!record) {
          console.warn('⚠️ No existe el perfil del cliente en /clients');
          setLoading(false);
          return;
        }

        setUserProfile(record);

        // 2️⃣ Facturas del cliente
        const invoicesRef = collection(db, 'clients', record.id, 'invoices');
        const invoiceSnap = await getDocs(invoicesRef);
        const invoiceList = invoiceSnap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setInvoices(invoiceList);

      } catch (error) {
        console.error('❌ Error al cargar datos del cliente:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchClientData();
  }, [navigate]);

  // 🌀 Mientras carga
  if (loading) return <p>Cargando datos del cliente...</p>;

  // ✅ Hasta acá todo limpio y funcional
  // A partir de aquí sigue tu template (facturas, modal, propuestas, etc.)

  return (
    <div className="dashboard-container">
      <h2>Panel del Cliente</h2>

      {showAdminNotification && (
        <div className="notification-banner">
          <p className="text-sm text-blue-900 font-medium">
            📬 Tienes nuevos mensajes del administrador
          </p>
          <button
            className="mt-2 px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
            onClick={async () => {
              const auth = getAuth();
              const user = auth.currentUser;
              const q = query(
                collection(db, 'conversations', user.email.toLowerCase(), 'messages'),
                where('from', '==', 'admin'),
                where('read', '==', false)
              );

              const snapshot = await getDocs(q);
              const updates = snapshot.docs.map((docSnap) =>
                updateDoc(
                  doc(db, 'conversations', user.email.toLowerCase(), 'messages', docSnap.id),
                  { read: true }
                )
              );
              await Promise.all(updates);

              setShowAdminNotification(false);
              navigate('/clients/messages');
            }}
          >
            Ver mensajes
          </button>
        </div>
      )}


<div className="space-y-6">
  <h3 className="text-2xl font-semibold text-gray-800">Facturas</h3>

  {invoices.length === 0 ? (
    <p className="text-gray-500">No hay facturas registradas.</p>
  ) : (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {invoices.map((inv) => (
        <div
          key={inv.id}
          className="bg-white shadow-md border border-gray-200 rounded-lg p-5 transition hover:shadow-lg"
        >
          <div className="flex justify-between items-start">
            <div>
              <h4 className="text-lg font-bold text-gray-800">
                Factura #{inv.id.slice(-5)}
              </h4>
              <p className="text-sm text-gray-500">
                {inv.createdAt?.toDate?.().toLocaleDateString?.() || '—'}
              </p>
            </div>
            <span
              className={`text-xs px-2 py-1 rounded-full font-semibold capitalize ${
                inv.status === 'paid'
                  ? 'bg-green-100 text-green-700'
                  : inv.status === 'pending'
                  ? 'bg-yellow-100 text-yellow-700'
                  : 'bg-red-100 text-red-700'
              }`}
            >
              {inv.status === 'paid'
                ? 'Pagada'
                : inv.status === 'pending'
                ? 'Pendiente'
                : 'Cancelada'}
            </span>
          </div>

          <div className="mt-4 text-sm text-gray-700 space-y-1">
            <p><span className="font-medium">Total:</span> ${Number(inv.total || 0).toFixed(2)}</p>
            <p><span className="font-medium">Depósito:</span> ${Number(inv.deposit || 0).toFixed(2)}</p>
            <p><span className="font-medium">Saldo:</span> ${getRemaining(inv)}</p>
          </div>

          <div className="flex justify-between items-center mt-4">
            <button
              onClick={() => handleOpenModal(inv)}
              className="flex items-center text-orange-600 hover:text-orange-800 text-sm font-medium"
            >
              <EyeIcon className="w-5 h-5 mr-1" />
              Ver factura
            </button>

            {inv.status !== 'paid' && (
              <button
                onClick={() => alert('Función de pago pendiente')}
                className="flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                <CreditCardIcon className="w-5 h-5 mr-1" />
                Pagar
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  )}

  


      {/* MODAL FACTURA */}
{selectedInvoice && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">

    {/* ✅ ESTE DIV AHORA ES EL CONTENEDOR PARA PDF */}
    <div
      id="invoice-pdf"
      className="bg-white max-w-3xl w-full rounded-lg shadow-lg relative overflow-y-auto max-h-[90vh] p-6"
    >

      <button
        onClick={handleCloseModal}
        className="absolute top-4 right-4 text-gray-400 hover:text-red-600"
      >
        <XMarkIcon className="w-6 h-6" />
      </button>

            <h2 className="text-2xl font-bold mb-4">Factura #{selectedInvoice.id?.slice(-5)}</h2>

            <div className="flex flex-col md:flex-row justify-between mb-4 text-sm">
              <div>
                <img src="/logo-prwd.svg" alt="Puerto Rico Windows & Doors Logo" className="h-12 mb-2" />
                <p>Puerto Rico Windows & Doors</p>
                <p>info@puertoricowindows.com</p>
                <p>+1 (787) 123-4567</p>
              </div>

              <div>
                <p><strong>Cliente:</strong> {userProfile?.name}</p>
                <p>{userProfile?.email}</p>
                <p>{userProfile?.phone}</p>
                <p><strong>Fecha:</strong> {selectedInvoice.createdAt?.toDate?.().toLocaleDateString?.() || '—'}</p>
              </div>
            </div>

            {/* ITEMS DE LA FACTURA */}
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm border">
                <thead className="bg-gray-100 text-left">
                  <tr>
                    <th className="p-2 border">Servicio</th>
                    <th className="p-2 border">Descripción</th>
                    <th className="p-2 border">Precio</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedInvoice.items?.map((item, idx) => (
                    <tr key={idx}>
                      <td className="p-2 border">{item.name}</td>
                      <td className="p-2 border">{item.description}</td>
                      <td className="p-2 border">${parseFloat(item.price).toFixed(2)}</td>
                    </tr>
                  ))}

                  <tr>
                    <td colSpan="2" className="p-2 border text-right font-semibold">Subtotal</td>
                    <td className="p-2 border">${parseFloat(selectedInvoice.total).toFixed(2)}</td>
                  </tr>

                  <tr>
                    <td colSpan="2" className="p-2 border text-right">Depósito</td>
                    <td className="p-2 border">-${parseFloat(selectedInvoice.deposit).toFixed(2)}</td>
                  </tr>

                  {selectedInvoice.discount && (
                    <tr>
                      <td colSpan="2" className="p-2 border text-right">Descuento</td>
                      <td className="p-2 border">-${parseFloat(selectedInvoice.discount).toFixed(2)}</td>
                    </tr>
                  )}

                  {selectedInvoice.payments?.length > 0 && (
                    <>
                      <tr>
                        <td colSpan="3" className="p-2 border font-semibold">Pagos realizados</td>
                      </tr>
                      {selectedInvoice.payments.map((p, i) => (
                        <tr key={i}>
                          <td colSpan="2" className="p-2 border">{new Date(p.date).toLocaleDateString()}</td>
                          <td className="p-2 border">-${parseFloat(p.amount).toFixed(2)}</td>
                        </tr>
                      ))}
                    </>
                  )}

                  <tr>
                    <td colSpan="2" className="p-2 border text-right font-semibold">Saldo</td>
                    <td className="p-2 border text-red-600 font-bold">
                      ${getRemaining(selectedInvoice)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="flex justify-end mt-6 gap-3">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 text-gray-600 border rounded hover:bg-gray-100"
              >
                Cerrar
              </button>
              <button
                onClick={generatePDF}
                className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600"
              >
                <ArrowDownTrayIcon className="w-5 h-5" />
                Descargar PDF
              </button>
              <button
                onClick={() => alert('Función de pago pendiente')}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                <CreditCardIcon className="w-5 h-5" />
                Pagar balance
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default ClientsDashboard;
